/**
 * Cookie Sync Middleware
 * 
 * Syncs Redux cart and wishlist state to:
 * - Browser cookies (for all users)
 * - Backend API (for guest/user types)
 * 
 * Flow:
 * - Random User: Only syncs to cookies
 * - Guest/User: Syncs via API (cookies are backup)
 */

import { Middleware } from '@reduxjs/toolkit';
import { setCookie } from '../utilities/cookieUtils';

export const cookieSyncMiddleware: Middleware =
  (store: any) => (next: any) => (action: any) => {
    // Call the action
    const result = next(action);

    // Get updated state
    const state = store.getState() as any;

    // Sync cart to cookie when cart actions occur.
    // IMPORTANT: Do NOT overwrite cookies during persist/REHYDRATE because
    // persisted Redux may contain stale cart data and can overwrite the
    // more up-to-date cookie (which some code updates directly).
    if (action.type.startsWith('cart/')) {
      try {
        const cartItems = state.cart.items;
        if (cartItems && cartItems.length > 0) {
          setCookie('cart', cartItems, 7);
        } else {
          // Clear cookie if cart is empty
          document.cookie = 'cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
      } catch (error) {
        console.error('Error syncing cart to cookie:', error);
      }
    }

    // Sync wishlist to cookie
    // Note: API sync is handled in useWishlist hook directly
    // This middleware only keeps cookies in sync as backup
    if (action.type.startsWith('wishlist/')) {
      try {
        const wishlistItems = state.wishlist.items;
        if (wishlistItems && wishlistItems.length > 0) {
          setCookie('wishlist', wishlistItems, 7);
        } else {
          // Clear cookie if wishlist is empty
          document.cookie = 'wishlist=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
      } catch (error) {
        console.error('Error syncing wishlist to cookie:', error);
      }
    }

    return result;
  };
